import React, { useState } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import getCurriculumAssessmentsStore from 'graph/state/query-hooks/use-curriculum-assessments';
import StudentBenchmarkList from './StudentBenchmarkList';
import StudentUnitList from './StudentUnitList';
import ObservationalDropdown from './ObservationalDropdown';
import HideableTabPanel from '../../../common/HideableTabPanel';
import { AssessmentTabs, BenchmarkAssessment, Checklist, Student, UnitAssessment } from 'types';
import SimpleStudentsList from './SimpleStudentsList';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    tabs: {
      backgroundColor: 'transparent',
      color: theme.palette.black.main,
      marginTop: theme.spacing(4),

      '& .MuiTabs-indicator': {
        display: 'none',
      },

      '& .Mui-selected': {
        backgroundColor: 'white',
        border: `1px solid ${(theme.palette as any).grey[600]}`,
        borderBottomColor: 'white',
      },

      '& .MuiTab-root': {
        borderRadius: '10px 10px 0 0 ',
        fontSize: '1.25rem',
        fontWeight: 'bold',
        paddingTop: 0,
        paddingBottom: 0,
        maxWidth: 'unset',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
      },
    },
    section: {
      backgroundColor: 'white',
      marginTop: '-1px',
      marginBottom: theme.spacing(8),
      border: `1px solid ${(theme.palette as any).grey[600]}`,
    },
  };
});

type LocalProps = {
  students: Student[];
  curriculumId: number;
  classId: number;
  tabSelection: string;
};

const AssessmentsTabulatedMenu = (props: LocalProps): React.ReactElement => {
  const [tabSelection, setTabSelection] = useState<string>(props.tabSelection);
  const [curriculum] = getCurriculumAssessmentsStore(props.curriculumId);
  const classes = useStyles();

  if (curriculum.isLoading) {
    return <></>;
  }

  const benchmarksExist =
    curriculum?.benchmarkAssessments && curriculum.benchmarkAssessments.length > 0;
  const unitsExist = curriculum?.unitAssessments && curriculum.unitAssessments.length > 0;
  const checklistsExist = curriculum && curriculum.checklists && curriculum.checklists.length > 0;

  if (tabSelection == AssessmentTabs.UNIT && !unitsExist) {
    setTabSelection('Benchmark');
  }

  return (
    <>
      {!benchmarksExist && !unitsExist && !checklistsExist && (
        <SimpleStudentsList students={props.students} />
      )}
      {(benchmarksExist || unitsExist || checklistsExist) && (
        <>
          <Tabs
            value={tabSelection}
            onChange={(_, value) => setTabSelection(value)}
            className={classes.tabs}
          >
            {benchmarksExist && (
              <Tab label={t('class.benchmark')} value={AssessmentTabs.BENCHMARK} />
            )}
            {unitsExist && <Tab label={t('class.unitAssessment')} value={AssessmentTabs.UNIT} />}
            {checklistsExist && (
              <Tab label={t('class.observationalChecklists')} value={AssessmentTabs.CHECKLIST} />
            )}
          </Tabs>
          <section className={classes.section}>
            {benchmarksExist && (
              <HideableTabPanel index={AssessmentTabs.BENCHMARK} value={tabSelection}>
                <StudentBenchmarkList
                  students={props.students}
                  curriculumId={props.curriculumId}
                  classId={props.classId}
                  benchmarkAssessments={curriculum.benchmarkAssessments as BenchmarkAssessment[]}
                  visible={tabSelection === AssessmentTabs.BENCHMARK}
                />
              </HideableTabPanel>
            )}
            {unitsExist && (
              <HideableTabPanel index={AssessmentTabs.UNIT} value={tabSelection}>
                <StudentUnitList
                  students={props.students}
                  curriculumId={props.curriculumId}
                  classId={props.classId}
                  unitAssessments={curriculum.unitAssessments as UnitAssessment[]}
                  visible={tabSelection === AssessmentTabs.UNIT}
                />
              </HideableTabPanel>
            )}
            {checklistsExist && (
              <HideableTabPanel index={AssessmentTabs.CHECKLIST} value={tabSelection}>
                <ObservationalDropdown
                  students={props.students}
                  curriculumId={props.curriculumId}
                  classId={props.classId}
                  checklists={curriculum.checklists as Checklist[]}
                />
              </HideableTabPanel>
            )}
          </section>
        </>
      )}
    </>
  );
};

export default AssessmentsTabulatedMenu;
